import { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useVerifyIdentityEmail } from '@/hooks/useIdentityEmails';
import { Button } from '@/components/ui/button';

/** Landing page for the link in an identity-email verification email
 * (`/account/verify-email?token=…`). Confirms the address for sign-in/recovery. */
export default function VerifyIdentityEmail() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const verify = useVerifyIdentityEmail();
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current || !token) return;
    fired.current = true;
    verify.mutate(token);
  }, [token, verify]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="bg-card border border-border rounded-lg p-8 max-w-md w-full text-center space-y-4">
        {!token ? (
          <>
            <XCircle className="h-10 w-10 text-destructive mx-auto" />
            <h1 className="text-lg font-semibold">Missing verification token</h1>
            <p className="text-sm text-muted-foreground">This link is incomplete. Try the link in your email again.</p>
          </>
        ) : verify.isPending || verify.isIdle ? (
          <>
            <Loader2 className="h-10 w-10 text-muted-foreground mx-auto animate-spin" />
            <h1 className="text-lg font-semibold">Confirming your email…</h1>
          </>
        ) : verify.isSuccess ? (
          <>
            <CheckCircle2 className="h-10 w-10 text-sev-4 mx-auto" />
            <h1 className="text-lg font-semibold">Email confirmed</h1>
            <p className="text-sm text-muted-foreground">
              You can now sign in and recover your account with this address. Manage it in your profile.
            </p>
          </>
        ) : (
          <>
            <XCircle className="h-10 w-10 text-destructive mx-auto" />
            <h1 className="text-lg font-semibold">Couldn't confirm this email</h1>
            <p className="text-sm text-muted-foreground">The link may have expired or already been used.</p>
          </>
        )}
        <Button asChild variant="outline">
          <Link to="/account/emails">Go to email addresses</Link>
        </Button>
      </div>
    </div>
  );
}
